import { Copy, Triangle } from 'lucide-react';

import { Button, cn, Textarea } from '@src/components/ui';

import ItemSpecificsChecklist from './ItemSpecificsChecklist';


/** Write to the clipboard, awaiting the result. On success show the success toast;
 * on failure (or no clipboard API) surface an error toast instead of silently
 * succeeding. `notify` is the page toast fn: notify(message, variant). */
async function copyToClipboard(value, notify, successMessage = 'Copied') {
  try {
    await navigator.clipboard.writeText(value);
    notify(successMessage);
  }
  catch {
    notify('Copy failed — copy manually', 'danger');
  }
}


/** Assemble the plain-text "Copy all" blob from the (locally edited) fields. */
function assemble(channel, fields) {
  if (channel === 'ebay') {
    const specifics = (fields.item_specifics || [])
      .map((spec) => `${spec.name}: ${spec.value}`)
      .join('\n');
    return [
      fields.category?.path ? `Category: ${fields.category.path}` : '',
      `Title: ${fields.title || ''}`,
      specifics ? `\nItem specifics:\n${specifics}` : '',
      `\nDescription:\n${fields.description || ''}`,
    ]
      .filter(Boolean)
      .join('\n');
  }
  // Facebook + Craigslist: plain-text title + description (no category/item specifics).
  return [`Title: ${fields.title || ''}`, `\nDescription:\n${fields.description || ''}`].join('\n');
}


function hasFitmentSpecific(fields) {
  return (fields?.item_specifics || []).some((spec) => spec.name === 'Fitment' && spec.value);
}


function IconCopyButton({ value, label, onCopied, size = 14 }) {
  return (
    <Button
      variant="ghost"
      aria-label={label}
      className="grid size-auto shrink-0 place-items-center p-0 text-ink-subtle hover:bg-transparent hover:text-ink"
      onClick={() => copyToClipboard(value, onCopied)}
    >
      <Copy className="size-auto!" style={{ width: size, height: size }} />
    </Button>
  );
}


// Display-only: the eBay category is auto-mapped from the part type server-side
// (EbayCategoryMap). There is no user-facing category list/endpoint to pick from,
// so we show the resolved path as plain text — no dropdown affordance. A manual
// category override is deferred until a category-options source exists (#111).
function CategoryField({ category }) {
  const mapped = category?.mapped;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2">
        <span className="text-[11.5px] text-ink-subtle">eBay category</span>

        {mapped
          ? (
            <span className="rounded-full bg-success/13 px-2 py-[2px] text-[9.5px] font-semibold text-success">
              auto-mapped
            </span>
          )
          : (
            <span className="rounded-full bg-warning/13 px-2 py-[2px] text-[9.5px] font-semibold text-warning">
              not mapped — using default
            </span>
          )}
      </span>

      <span
        className={cn(
          'flex h-10 items-center rounded-lg border border-hairline-strong bg-surface-2 px-3',
        )}
      >
        <span className="truncate text-[13.5px] text-ink">{category?.path || '—'}</span>
      </span>
    </div>
  );
}


function TitleField({ value, limit, onChange, onCopied }) {
  const over = limit ? value.length > limit : false;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between">
        <span className="text-[11.5px] text-ink-subtle">Title</span>

        {limit
          ? (
            <span className={cn('text-[11px]', over ? 'text-danger' : 'text-ink-subtle')}>
              {`${value.length} / ${limit}`}
            </span>
          )
          : null}
      </span>

      <span
        className={cn(
          'flex h-10 items-center gap-2 rounded-lg border border-hairline-strong bg-surface-2 px-3',
          over && 'border-danger',
        )}
      >
        <input
          value={value}
          className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none"
          onChange={(event) => onChange(event.target.value)}
        />

        <IconCopyButton value={value} label="Copy title" onCopied={onCopied} />
      </span>
    </div>
  );
}


function SpecificsRows({ specifics, onChange, onCopied }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
        Item specifics
      </span>

      <div className="flex flex-col gap-[7px]">
        {specifics.map((spec, index) => (
          <span
            key={spec.name}
            className="flex items-center gap-2 rounded-lg border border-hairline bg-surface-2 px-3 py-2"
          >
            <span className="w-[88px] shrink-0 text-[12.5px] text-ink-subtle">{spec.name}</span>

            <input
              value={spec.value}
              className={cn(
                'min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none',
                spec.name === 'OE / Part #' && 'font-mono',
              )}
              onChange={(event) => {
                const next = specifics.map((entry, entryIndex) => (
                  entryIndex === index ? { ...entry, value: event.target.value } : entry
                ));
                onChange(next);
              }}
            />

            <IconCopyButton size={13} value={spec.value} label={`Copy ${spec.name}`} onCopied={onCopied} />
          </span>
        ))}
      </div>
    </div>
  );
}


function DescriptionField({ value, onChange, onCopied }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between">
        <span className="text-[11.5px] text-ink-subtle">Description</span>
        <IconCopyButton value={value} label="Copy description" onCopied={onCopied} />
      </span>

      <Textarea
        value={value}
        className={cn(
          'h-[84px] resize-none border-hairline-strong bg-surface-2 p-3',
          'text-[12.5px] leading-relaxed text-ink-muted',
        )}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}


/** Body for one channel tab, fed local (edited) fields. The eBay category +
 * item-specifics + thin-copy warning are gated to `channel === 'ebay'` (#317):
 * Facebook + Craigslist render title + description only. */
function ChannelBody(props) {
  const { channel, fields, onPatch, onCopied, seller, part, canWrite, onSaved } = props;
  const thin = channel === 'ebay' && !hasFitmentSpecific(fields);

  return (
    <div className="flex flex-col gap-3.5 px-[22px] py-4">
      {thin
        ? (
          <span
            className={cn(
              'flex items-start gap-2 rounded-lg border border-warning/35 bg-warning/10 px-3 py-2.5',
              'text-[12px] leading-snug text-warning',
            )}
          >
            <Triangle className="mt-0.5 size-3.5 shrink-0 fill-warning" />
            This part is missing details (fitment/photos). Copy will be thin — consider completing it first.
          </span>
        )
        : null}

      {channel === 'ebay' ? <CategoryField category={fields.category} /> : null}

      <TitleField
        limit={fields.title_limit}
        value={fields.title || ''}
        onCopied={onCopied}
        onChange={(title) => onPatch({ title })}
      />

      {channel === 'ebay'
        ? (
          <ItemSpecificsChecklist
            partId={part.id}
            canWrite={canWrite}
            sellerId={seller.id}
            onSaved={onSaved}
          />
        )
        : null}

      {channel === 'ebay'
        ? (
          <SpecificsRows
            specifics={fields.item_specifics || []}
            onCopied={onCopied}
            onChange={(item_specifics) => onPatch({ item_specifics })}
          />
        )
        : null}

      <DescriptionField
        value={fields.description || ''}
        onCopied={onCopied}
        onChange={(description) => onPatch({ description })}
      />
    </div>
  );
}

ChannelBody.displayName = 'ChannelBody';


export {
  assemble,
  ChannelBody,
  copyToClipboard,
};
